import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import DotsVerticalIcon from "@redotech/redo-icons/dots-vertical.svg";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { memo, useCallback } from "react";
import { SupportTicketActionsDropdown } from "../../../actions/support-ticket-actions-dropdown";
import { useSupportTicketsTableContext } from "../../support-tickets-table-context";

interface SupportTicketActionsMenuProps {
  supportTicket: SupportTicketListItemResponse;
}

export const SupportTicketActionsMenu = memo(function SupportTicketActionsMenu({
  supportTicket,
}: SupportTicketActionsMenuProps) {
  const {
    actionMenuOpenForId,
    setActionMenuOpenForId,
    setMergeSupportTicket,
    setChangeSubjectSupportTicket,
  } = useSupportTicketsTableContext();

  const isOpen = actionMenuOpenForId === supportTicket.id;
  const setIsOpen = useCallback(
    (open: boolean) => setActionMenuOpenForId(open ? supportTicket.id : null),
    [setActionMenuOpenForId, supportTicket.id],
  );

  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);

  // The merge modal is rendered once at the table level (outside the rows) so
  // its clicks can't bubble to the row's navigation handler. Opening it just
  // records which ticket to merge into.
  const handleOpenMergeModal = useCallback(
    () => setMergeSupportTicket(supportTicket),
    [setMergeSupportTicket, supportTicket],
  );

  // Like the merge modal, the change-subject modal lives at the table level so
  // its focus trap can't fight this row's dropdown. Selecting the action just
  // records which ticket to edit.
  const handleOpenChangeSubjectModal = useCallback(
    () => setChangeSubjectSupportTicket(supportTicket),
    [setChangeSubjectSupportTicket, supportTicket],
  );

  return (
    <DropdownMenu onOpenChange={setIsOpen} open={isOpen}>
      <DropdownMenuTrigger
        className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <DotsVerticalIcon className="h-4 w-4" />
      </DropdownMenuTrigger>
      <SupportTicketActionsDropdown
        isOpen={isOpen}
        onChangeSubject={handleOpenChangeSubjectModal}
        onClose={handleClose}
        onMergeTicket={handleOpenMergeModal}
        supportTicket={supportTicket}
        supportTicketId={supportTicket.id}
      />
    </DropdownMenu>
  );
});
